import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, createContext, useContext, type ReactNode } from 'react';
import maplibregl, { type Map as MapLibreMap, type MapOptions, type GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { cn } from '../../lib/utils';

type MapContextValue = { map: MapLibreMap | null; isLoaded: boolean };
const MapContext = createContext<MapContextValue | null>(null);

export function useMap() {
  const ctx = useContext(MapContext);
  if (!ctx) throw new Error('useMap must be used within <Map>');
  return ctx;
}

export type MapStyleOption = string | maplibregl.StyleSpecification;

export type MapProps = {
  className?: string;
  children?: ReactNode;
  styles: { light: MapStyleOption; dark: MapStyleOption };
  theme?: 'light' | 'dark';
} & Omit<MapOptions, 'container' | 'style'>;

export type MapRef = MapLibreMap;

export const Map = forwardRef<MapRef, MapProps>(function Map(
  { className, styles, theme = 'light', children, ...options },
  ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const style = useMemo(() => (theme === 'dark' ? styles.dark : styles.light), [styles, theme]);

  useImperativeHandle(ref, () => mapRef.current as MapLibreMap);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let map: MapLibreMap | null = null;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style,
        attributionControl: { compact: true },
        renderWorldCopies: false,
        ...options,
      });
      mapRef.current = map;
      setMapError(null);

      const onLoad = () => setIsLoaded(true);
      map.on('load', onLoad);

      return () => {
        map?.off('load', onLoad);
        map?.remove();
        mapRef.current = null;
        setIsLoaded(false);
      };
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError('Map is unavailable on this device/browser right now.');
      mapRef.current = null;
      setIsLoaded(false);
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switch style when theme/styles change — skip the first call because the
  // map was already created with this style and calling setStyle again blows
  // away any sources/layers that children may have added.
  const initialStyleRef = useRef(true);
  useEffect(() => {
    if (initialStyleRef.current) {
      initialStyleRef.current = false;
      return;
    }
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(style);
  }, [style]);

  const ctx = useMemo(() => ({ map: mapRef.current, isLoaded }), [isLoaded]);

  return (
    <div className={cn('relative h-full w-full', className)} data-map-theme={theme}>
      <div ref={containerRef} className={cn('absolute inset-0', mapError ? 'hidden' : undefined)} />
      {mapError && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 px-4 text-center text-sm text-slate-700 dark:bg-slate-900 dark:text-slate-200">
          {mapError}
        </div>
      )}
      <MapContext.Provider value={ctx}>{children}</MapContext.Provider>
    </div>
  );
});

export function MapControls({
  position = 'top-right',
  showLocate = true,
}: {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  showLocate?: boolean;
}) {
  const { map, isLoaded } = useMap();

  useEffect(() => {
    if (!map || !isLoaded) return;

    const control = new maplibregl.NavigationControl({ showCompass: true, showZoom: true });
    map.addControl(control, position);

    return () => {
      map.removeControl(control);
    };
  }, [isLoaded, map, position]);

  useEffect(() => {
    if (!map || !isLoaded || !showLocate) return;

    // MapLibre has a built-in geolocate control.
    const control = new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: false,
      showUserLocation: true,
    });
    map.addControl(control, position);

    return () => {
      map.removeControl(control);
    };
  }, [isLoaded, map, position, showLocate]);

  return null;
}

export function MapGeoJsonLayer({
  id,
  data,
  lineColor = '#3b82f6',
  lineWidth = 3,
  lineOpacity = 0.8,
  colorByType = false,
}: {
  id: string;
  data: GeoJSON.FeatureCollection;
  lineColor?: string;
  lineWidth?: number;
  lineOpacity?: number;
  colorByType?: boolean;
}) {
  const { map, isLoaded } = useMap();
  const sourceId = `${id}-source`;
  const layerId = `${id}-layer`;

  // Add or update the source and layer whenever map loads, data changes, or style changes
  useEffect(() => {
    if (!map || !isLoaded) return;

    const addSourceAndLayer = () => {
      // Ensure source exists
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, { type: 'geojson', data });
      } else {
        // Source exists, update its data
        (map.getSource(sourceId) as GeoJSONSource).setData(data as any);
      }

      // Ensure layer exists
      if (!map.getLayer(layerId)) {
        const paintColor = colorByType
          ? ['match', ['get', 'waterwayType'], 'canal', '#3b82f6', 'river', '#0ea5e9', '#3b82f6'] as any
          : lineColor;

        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': paintColor,
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1, 10, lineWidth, 14, lineWidth + 2],
            'line-opacity': lineOpacity,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        });
      }
    };

    // Add immediately
    addSourceAndLayer();

    // Re-add after style changes (setStyle removes all sources/layers)
    const onStyleData = () => {
      if (!map.getSource(sourceId)) {
        addSourceAndLayer();
      }
    };
    map.on('styledata', onStyleData);

    return () => {
      map.off('styledata', onStyleData);
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch {
        // Map may already be removed
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, data, colorByType, lineColor, lineWidth, lineOpacity]);

  return null;
}
